/**
 * Mint AI Chat Service
 *
 * Handles token fetching, caching, and widget open/close.
 */

import { getAiChatGetToken } from "api/api/generated/AshtromApi"

const TOKEN_STORAGE_KEY = "mintAiChatToken"
const TOKEN_EXPIRY_KEY = "mintAiChatTokenExpiry"
const TOKEN_VALIDITY_DAYS = 20

const clearStoredToken = (): void => {
  if (typeof window === "undefined") return
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)
  } catch {
    // localStorage unavailable
  }
}

const getStoredToken = (): string | null => {
  if (typeof window === "undefined") return null
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (!token || !expiresAt) return null
    if (Date.now() < parseInt(expiresAt, 10)) return token
    clearStoredToken()
    return null
  } catch {
    return null
  }
}

const storeToken = (token: string): void => {
  if (typeof window === "undefined") return
  try {
    const expiresAt = Date.now() + TOKEN_VALIDITY_DAYS * 24 * 60 * 60 * 1000
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString())
  } catch {
    // localStorage unavailable
  }
}

const fetchNewToken = async (): Promise<string | null> => {
  try {
    const response = await getAiChatGetToken()
    const token = response.data?.body
    if (token && typeof token === "string") {
      storeToken(token)
      return token
    }
    console.error("[MintAI] Invalid token response:", response.data)
    return null
  } catch (error) {
    console.error("[MintAI] Error fetching token:", error)
    return null
  }
}

export const getAiChatToken = async (): Promise<string | null> => {
  const storedToken = getStoredToken()
  if (storedToken) return storedToken
  return await fetchNewToken()
}

export const openAiChat = async (initialPrompt?: string): Promise<void> => {
  if (typeof window === "undefined") return
  if (!window.mintAiWidget) {
    console.error("[MintAI] Widget not loaded")
    return
  }
  const token = await getAiChatToken()
  window.mintAiWidget.open(token || "", initialPrompt)
}

export const closeAiChat = (): void => {
  if (typeof window !== "undefined" && window.mintAiWidget) {
    window.mintAiWidget.close()
  }
}

export const clearAiChatToken = (): void => {
  clearStoredToken()
}

/* eslint-disable no-unused-vars */
declare global {
  interface Window {
    mintAiWidget?: {
      open: (token: string, initialPrompt?: string) => void
      close: () => void
      show: () => void
      remove: () => void
      updateConfig: (config: Record<string, unknown>) => void
      getConfig: () => Record<string, unknown>
    }
    next?: {
      router?: {
        push: (url: string) => void
      }
    }
    MintAIChatWidget?: {
      create: (config: Record<string, unknown>, url: string) => Window["mintAiWidget"]
    }
  }
}
/* eslint-enable no-unused-vars */
